import { useEffect, useRef } from "react";

const DEFAULT_PREVIEW_WIDTH = 450;
const BASE_PREVIEW_FONT_SIZE = 20;

const drawBrandingPreview = ({
  canvas,
  width = DEFAULT_PREVIEW_WIDTH,
  horizontalPadding,
  headerHeight,
  logoImg,
  companyName,
  designName,
  logoAlignment,
  textAlignment,
  logoMargin,
  textMargin,
  logoScale,
  textScale,
}) => {
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  const headerHeightPx = headerHeight;
  const paddingPx = horizontalPadding;

  const scaledFontSize = BASE_PREVIEW_FONT_SIZE * (textScale / 100);

  canvas.width = width;
  canvas.height = headerHeightPx + 2;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = "#a7a7a7";
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, canvas.width, canvas.height);

  const hasBrandingContent = companyName.trim() || logoImg;
  if (!hasBrandingContent) return;

  const headerCenterY = headerHeightPx / 2;

  if (logoImg) {
    const logoMaxHeight = headerHeightPx * (logoScale / 100);
    const logoMaxWidth = width - 2 * paddingPx;
    const scale = Math.min(
      1,
      logoMaxHeight / logoImg.height,
      logoMaxWidth / logoImg.width
    );
    const drawHeight = logoImg.height * scale;
    const drawWidth = logoImg.width * scale;
    const logoY = headerCenterY - drawHeight / 2;

    let logoX = paddingPx + logoMargin;
    if (logoAlignment === "center") {
      logoX = width / 2 - drawWidth / 2;
    } else if (logoAlignment === "right") {
      logoX = width - paddingPx - logoMargin - drawWidth;
    }

    ctx.drawImage(logoImg, logoX, logoY, drawWidth, drawHeight);
  }

  const text = companyName.trim() || designName;
  let textX = paddingPx + textMargin;
  let textAnchor = "left";

  if (textAlignment === "center") {
    textX = width / 2;
    textAnchor = "center";
  } else if (textAlignment === "right") {
    textX = width - paddingPx - textMargin;
    textAnchor = "right";
  }

  ctx.fillStyle = "#0f172a";
  ctx.font = `bold ${scaledFontSize}px system-ui`;
  ctx.textBaseline = "middle";
  ctx.textAlign = textAnchor;
  ctx.fillText(text, textX, headerCenterY);

  ctx.strokeStyle = "#dc2626";
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(width / 2, 0);
  ctx.lineTo(width / 2, headerHeightPx);
  ctx.stroke();
  ctx.setLineDash([]);
};

export default function BrandingPreviewCanvas({
  logoImg,
  companyName,
  designName,
  logoAlignment,
  textAlignment,
  logoMargin,
  textMargin,
  logoScale,
  textScale,
  horizontalPadding,
  headerHeight,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    drawBrandingPreview({
      canvas: canvasRef.current,
      horizontalPadding,
      headerHeight,
      logoImg,
      companyName,
      designName,
      logoAlignment,
      textAlignment,
      logoMargin,
      textMargin,
      logoScale,
      textScale,
    });
  }, [
    horizontalPadding,
    headerHeight,
    logoImg,
    companyName,
    designName,
    logoAlignment,
    textAlignment,
    logoMargin,
    textMargin,
    logoScale,
    textScale,
  ]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        border: "1px solid var(--border)",
        width: "100%",
        height: `${headerHeight + 2}px`,
        maxWidth: `${DEFAULT_PREVIEW_WIDTH}px`,
        margin: "10px 0",
        display: "block",
      }}
    />
  );
}
